import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';

const LOADING_ROTATION_INTERVAL_MS = 3000;

const DEFAULT_HEADER_CONFIG = {
  showCloseButton: true,
  showBackButton: false,
  showLogo: true,
  showLogoButton: false,
  onBack: null,
  onLogo: null,
  onClose: null,
  children: null,
  centerContent: null,
  logo: null,
  logoSrc: null,
  logoAlt: 'You',
  closeIcon: null,
  closeSrc: null,
  closeAlt: 'Fechar',
  backIcon: null,
  backSrc: null,
  backAlt: 'Voltar',
  footer: null,
};

const YouHeaderContext = createContext({
  isLoading: true,
  loadingDescription: '',
  showLoading: () => undefined,
  hideLoading: () => undefined,
  isSuccess: false,
  successMessage: '',
  showSuccess: () => undefined,
  ...DEFAULT_HEADER_CONFIG,
  setHeaderConfig: () => undefined,
});

const arraysShallowEqual = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

export const YouHeaderProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState([]);
  const [loadingIndex, setLoadingIndex] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [headerConfig, setHeaderConfig] = useState(DEFAULT_HEADER_CONFIG);
  const rotationTimerRef = useRef(null);
  const loadingMessagesRef = useRef([]);
  useEffect(() => {
    loadingMessagesRef.current = loadingMessages;
  }, [loadingMessages]);

  const clearRotationTimer = useCallback(() => {
    if (rotationTimerRef.current) {
      clearTimeout(rotationTimerRef.current);
      rotationTimerRef.current = null;
    }
  }, []);

  const showLoading = useCallback(
    (descriptionOrMessages = '') => {
      const normalized = Array.isArray(descriptionOrMessages)
        ? descriptionOrMessages.filter((m) => typeof m === 'string' && m.length > 0)
        : [descriptionOrMessages];
      const nextMessages = normalized.length > 0 ? normalized : [''];

      setIsSuccess(false);
      setSuccessMessage('');

      if (arraysShallowEqual(nextMessages, loadingMessagesRef.current)) {
        setIsLoading(true);
        return;
      }

      clearRotationTimer();
      setIsLoading(true);
      setLoadingMessages(nextMessages);
      setLoadingIndex(0);
    },
    [clearRotationTimer],
  );

  const showSuccess = useCallback(
    (message = '') => {
      clearRotationTimer();
      setIsLoading(false);
      setLoadingMessages([]);
      setLoadingIndex(0);
      setIsSuccess(true);
      setSuccessMessage(typeof message === 'string' ? message : '');
    },
    [clearRotationTimer],
  );

  const hideLoading = useCallback(() => {
    clearRotationTimer();
    setIsLoading(false);
    setLoadingMessages([]);
    setLoadingIndex(0);
    setIsSuccess(false);
    setSuccessMessage('');
  }, [clearRotationTimer]);

  useEffect(() => {
    if (!isLoading || loadingMessages.length < 2) {
      return undefined;
    }
    if (loadingIndex >= loadingMessages.length - 1) {
      return undefined;
    }
    rotationTimerRef.current = setTimeout(() => {
      setLoadingIndex((prev) =>
        prev < loadingMessages.length - 1 ? prev + 1 : prev,
      );
    }, LOADING_ROTATION_INTERVAL_MS);
    return () => {
      if (rotationTimerRef.current) {
        clearTimeout(rotationTimerRef.current);
        rotationTimerRef.current = null;
      }
    };
  }, [isLoading, loadingMessages, loadingIndex]);

  const loadingDescription = loadingMessages[loadingIndex] ?? '';

  const updateHeaderConfig = useCallback((partial) => {
    if (partial === null || partial === undefined) {
      setHeaderConfig(DEFAULT_HEADER_CONFIG);
      return;
    }
    setHeaderConfig((prev) => ({ ...prev, ...partial }));
  }, []);

  const value = useMemo(
    () => ({
      isLoading,
      loadingDescription,
      showLoading,
      hideLoading,
      isSuccess,
      successMessage,
      showSuccess,
      ...headerConfig,
      setHeaderConfig: updateHeaderConfig,
    }),
    [
      isLoading,
      loadingDescription,
      showLoading,
      hideLoading,
      isSuccess,
      successMessage,
      showSuccess,
      headerConfig,
      updateHeaderConfig,
    ],
  );

  return (
    <YouHeaderContext.Provider value={value}>
      {children}
    </YouHeaderContext.Provider>
  );
};

YouHeaderProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useYouHeader = () => useContext(YouHeaderContext);

export default YouHeaderProvider;
